/**
 * Ten sam ekran ustawienia hasła obsługuje panel i aplikację HotelSpot Staff.
 * Backend (v3-sendPanelPasswordReset) dokleja `app=staff` do linku, gdy reset zlecono z aplikacji.
 */
export type ResetPasswordApp = 'panel' | 'staff';

export const STAFF_APP_PARAM = 'app';

// Parametr czytany defensywnie: tylko dokładnie 'staff' zmienia wariant, każda inna wartość to panel.
export const getResetPasswordApp = (searchParams: URLSearchParams): ResetPasswordApp => (
  searchParams.get(STAFF_APP_PARAM)?.trim().toLowerCase() === 'staff' ? 'staff' : 'panel'
);

export type ResetPasswordFlow = 'invite' | 'reset';

/**
 * Dokąd przejść po udanym ustawieniu hasła. `null` = zostajemy na ekranie potwierdzenia:
 * pracownik Staff loguje się w aplikacji mobilnej, więc nie wysyłamy go do logowania panelu.
 */
export const getPostResetRedirect = (app: ResetPasswordApp, flow: ResetPasswordFlow): string | null => {
  if (app === 'staff') return null;
  return flow === 'invite' ? '/' : '/login';
};

/* Co jest nie tak z linkiem: 'expired' = trzeba poprosić o nowy, 'disabled' = konto wyłączone (nowy link nie pomoże). */
export type ResetLinkProblem = 'expired' | 'disabled';

const EXPIRED_LINK_CODES = new Set([
  'auth/expired-action-code',
  'auth/invalid-action-code',
  'auth/user-not-found',
]);

export const getResetLinkProblem = (error: unknown): ResetLinkProblem | null => {
  const code = (error as { code?: unknown } | null)?.code;
  if (typeof code !== 'string') return null;
  if (code === 'auth/user-disabled') return 'disabled';
  return EXPIRED_LINK_CODES.has(code) ? 'expired' : null;
};

/* Link bez oobCode i tokenu: panel wraca do logowania (jak dotąd), Staff pokazuje ekran z instrukcją. */
export const getMissingLinkAction = (app: ResetPasswordApp): 'redirectToLogin' | 'showLinkError' => (
  app === 'staff' ? 'showLinkError' : 'redirectToLogin'
);

export type ResetScreen =
  | 'verifying'
  | 'form'
  | 'success'
  | 'tokenError'
  | 'staffSuccess'
  | 'staffLinkExpired'
  | 'staffAccountDisabled';

interface ResetScreenInput {
  app: ResetPasswordApp;
  isVerifying: boolean;
  resetSuccess: boolean;
  linkProblem: ResetLinkProblem | null;
  tokenInvalid: boolean;
}

/**
 * Który ekran pokazać. W Staff sukces ma pierwszeństwo przed błędem linku: spóźniona weryfikacja kodu
 * (np. ponowny efekt w Strict Mode po confirmPasswordReset) widzi już zużyty kod i nie może przykryć potwierdzenia.
 */
export const getResetScreen = ({
  app, isVerifying, resetSuccess, linkProblem, tokenInvalid,
}: ResetScreenInput): ResetScreen => {
  if (isVerifying) return 'verifying';
  if (app === 'staff') {
    if (resetSuccess) return 'staffSuccess';
    if (linkProblem === 'disabled') return 'staffAccountDisabled';
    if (linkProblem === 'expired' || tokenInvalid) return 'staffLinkExpired';
    return 'form';
  }
  if (tokenInvalid) return 'tokenError';
  if (resetSuccess) return 'success';
  return 'form';
};
